import {
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

export type { CountryCode };

/** Product default for all tenants — India (+91). */
export const DEFAULT_PHONE_COUNTRY: CountryCode = 'IN';

/** @deprecated Use DEFAULT_PHONE_COUNTRY */
const FALLBACK_COUNTRY: CountryCode = DEFAULT_PHONE_COUNTRY;

/** ISO-2 → flag emoji (🇮🇳). */
export function countryFlagEmoji(iso: string): string {
  const code = iso.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '🏳️';
  return code.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

export function toNationalDigits(raw: string | null | undefined): string {
  return String(raw ?? '').replace(/\D/g, '');
}

/**
 * Default country for new phone fields — always IN (+91).
 * Device locale is ignored so stored national numbers are not mis-parsed.
 */
export function detectDefaultPhoneCountry(): CountryCode {
  return DEFAULT_PHONE_COUNTRY;
}

export function normalizePhoneCountry(
  iso: string | null | undefined,
): CountryCode {
  const code = String(iso ?? '').trim().toUpperCase();
  if (code && isSupportedCountry(code)) return code;
  return FALLBACK_COUNTRY;
}

export function isValidNationalPhone(
  phone: string | null | undefined,
  country: string | null | undefined,
): boolean {
  const digits = toNationalDigits(phone);
  if (!digits) return false;
  const cc = normalizePhoneCountry(country);
  try {
    return isValidPhoneNumber(digits, cc);
  } catch {
    return false;
  }
}

/**
 * Split a stored phone into national digits + country for edit forms.
 *
 * Prefer the country hint (default IN) as a national number first so values
 * like `9726816391` are not misread as Israel (+972) via `+${digits}`.
 */
export function parseStoredPhone(
  phone: string | null | undefined,
  countryHint?: string | null,
): { phone: string; country: CountryCode } {
  const digits = toNationalDigits(phone);
  const hint = normalizePhoneCountry(countryHint ?? DEFAULT_PHONE_COUNTRY);
  if (!digits) return { phone: '', country: hint };

  // 1) National number for the hint country (covers normal IN 10-digit mobiles).
  try {
    const asNational = parsePhoneNumberFromString(digits, hint);
    if (asNational?.isValid()) {
      return {
        phone: asNational.nationalNumber,
        country: asNational.country ?? hint,
      };
    }
  } catch {
    /* fall through */
  }

  // 2) Stored with hint dial prefix (e.g. 91XXXXXXXXXX).
  const dial = getCountryCallingCode(hint);
  if (digits.startsWith(dial) && digits.length > dial.length) {
    const national = digits.slice(dial.length);
    if (isValidNationalPhone(national, hint)) {
      return { phone: national, country: hint };
    }
  }

  // 3) Explicit E.164 only when long enough to include a country calling code.
  //    Avoids `+972…` / `+98…` false positives on 10-digit national mobiles.
  if (digits.length >= 11) {
    try {
      const withPlus = parsePhoneNumberFromString(`+${digits}`);
      if (withPlus?.isValid() && withPlus.country) {
        return {
          phone: withPlus.nationalNumber,
          country: withPlus.country,
        };
      }
    } catch {
      /* fall through */
    }
  }

  return { phone: digits, country: hint };
}

export type PhoneCountryOption = {
  value: CountryCode;
  /** Full searchable label */
  label: string;
  dialCode: string;
  flag: string;
};

let cachedOptions: PhoneCountryOption[] | null = null;

export function listPhoneCountryOptions(): PhoneCountryOption[] {
  if (cachedOptions) return cachedOptions;
  const countries = getCountries();
  cachedOptions = countries
    .map((iso) => {
      const dial = getCountryCallingCode(iso);
      const flag = countryFlagEmoji(iso);
      return {
        value: iso,
        dialCode: dial,
        flag,
        label: `${flag} +${dial} ${iso}`,
      };
    })
    .sort((a, b) => {
      if (a.value === 'IN') return -1;
      if (b.value === 'IN') return 1;
      return a.value.localeCompare(b.value);
    });
  return cachedOptions;
}

export function dialCodeForCountry(country?: string | null): string {
  return getCountryCallingCode(normalizePhoneCountry(country));
}
